import { AnimatePresence, motion } from "framer-motion";
import { FC } from "react";
import { MakeGenerics, Navigate, Outlet, useSearch } from "react-location";
import LOGINIMAGE from "assets/images/background.jpeg";
import { currentTokenVar } from "_graphql/cache/auth";

import { useReactiveVar } from "@apollo/client";
import { Check, Sparkles } from "lucide-react";

export type AuthLocationGenerics = MakeGenerics<{
    Search: {
        redirect?: string;
    };
}>;

const AuthLayout: FC = () => {
    const search = useSearch<AuthLocationGenerics>();
    const currentToken = useReactiveVar(currentTokenVar);

    if (currentToken) {
        return <Navigate to={search?.redirect ?? "/"} replace />;
    }

    return (
        <>
            <main className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-teal-50/20 md:px-0 px-2 w-full flex justify-between items-center overflow-hidden">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="w-full lg:w-1/2 h-screen flex justify-center items-center relative">
                    <Outlet />
                </motion.div>

                {/* Right Side - Marketing Content */}
                <div className="w-0 hidden lg:flex h-screen relative lg:w-1/2 bg-gradient-to-br from-white to-[#2e5cb8]">
                    <div className="absolute inset-0 bg-black opacity-60 z-10" />
                    {/* Globe/Visual Background */}
                    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 1, delay: 0.2 }}
                            className="relative w-[500px] h-[500px]">
                            {/* Animated rings */}
                            <motion.div
                                animate={{
                                    rotate: 360,
                                    scale: [1, 1.1, 1],
                                }}
                                transition={{
                                    rotate: {
                                        duration: 20,
                                        repeat: Infinity,
                                        ease: "linear",
                                    },
                                    scale: {
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    },
                                }}
                                className="absolute inset-0 rounded-full border-2 border-teal-200/30"
                            />
                            <motion.div
                                animate={{
                                    rotate: -360,
                                    scale: [1, 1.05, 1],
                                }}
                                transition={{
                                    rotate: {
                                        duration: 15,
                                        repeat: Infinity,
                                        ease: "linear",
                                    },
                                    scale: {
                                        duration: 2.5,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                        delay: 0.5,
                                    },
                                }}
                                className="absolute inset-8 rounded-full border-2 border-blue-200/40"
                            />

                            {/* Center globe */}
                            <motion.div
                                animate={{
                                    scale: [1, 1.02, 1],
                                    boxShadow: [
                                        "0 0 60px rgba(20, 184, 166, 0.3)",
                                        "0 0 80px rgba(59, 130, 246, 0.4)",
                                        "0 0 60px rgba(20, 184, 166, 0.3)",
                                    ],
                                }}
                                transition={{
                                    duration: 4,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                className="absolute inset-16 rounded-full bg-gradient-to-br from-teal-400/20 via-blue-500/30 to-green-400/20 backdrop-blur-sm flex items-center justify-center">
                                <img
                                    src={LOGINIMAGE}
                                    alt="Globe"
                                    className="w-full h-full object-cover rounded-full opacity-40"
                                />
                            </motion.div>

                            {/* Floating particles */}
                            {[...Array(8)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    animate={{
                                        y: [0, -20, 0],
                                        x: [0, Math.sin(i) * 10, 0],
                                        opacity: [0.3, 0.8, 0.3],
                                    }}
                                    transition={{
                                        duration: 3 + i * 0.5,
                                        repeat: Infinity,
                                        delay: i * 0.3,
                                    }}
                                    className="absolute w-2 h-2 bg-teal-400 rounded-full"
                                    style={{
                                        top: `${20 + i * 10}%`,
                                        left: `${10 + i * 12}%`,
                                    }}
                                />
                            ))}
                        </motion.div>
                    </div>

                    {/* Content */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="absolute inset-0 flex items-center justify-center p-12 w-full z-20">
                        <div className="max-w-2xl p-8 space-y-8">
                            {/* Powered by badge */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.6 }}
                                className="flex items-center gap-2 text-sm text-teal-700 font-medium bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full w-fit border border-teal-200/50">
                                <Sparkles className="w-4 h-4" />
                                Powered by Comfy AI
                            </motion.div>

                            {/* Title */}
                            <motion.h1
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.7 }}
                                className="text-6xl font-bold text-white">
                                Comfy{" "}
                                <span className="text-[#2e5cb8]">PlaceIT</span>
                                <br />
                                <span className="text-white">Dashboard</span>
                            </motion.h1>

                            {/* Description */}
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.8 }}
                                className="text-lg text-white leading-relaxed">
                                An AI-native platform for insurers to place
                                business, manage users, and surface real-time
                                insights across the Comfy ecosystem.
                            </motion.p>

                            {/* Features */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.9 }}
                                className="space-y-4">
                                {[
                                    {
                                        key: "layout.faster_placement",
                                        default: "Faster Business Placement",
                                    },
                                    {
                                        key: "layout.smart_management",
                                        default:
                                            "Smart Risk & Quote Management",
                                    },
                                    {
                                        key: "layout.seamless_collaboration",
                                        default:
                                            "Seamless Broker Collaboration",
                                    },
                                    {
                                        key: "layout.facultative_treaty",
                                        default:
                                            "Facultative & Treaty Made Simple",
                                    },
                                ].map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 1 + index * 0.1 }}
                                        className="flex items-center space-x-3 group">
                                        <motion.div
                                            whileHover={{ scale: 1.1 }}
                                            className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-teal-500 to-green-500 rounded-full flex items-center justify-center shadow-lg shadow-teal-500/30">
                                            <Check className="w-4 h-4 text-white" />
                                        </motion.div>
                                        <span className="text-lg text-white group-hover:text-teal-600 transition-colors">
                                            {feature.default}
                                        </span>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </main>
        </>
    );
};

export default AuthLayout;
